import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { api } from "../../lib/api";
import { useMeetingsView } from "../../lib/meetings-view";
import { queryKeys } from "../../lib/queryKeys";
import type { SalesMeeting } from "../../lib/sales-types";
import type { User } from "../../types";
import { MeetingPanel, type PanelFocus } from "../../components/meetings/MeetingPanel";
import { MeetingPeek } from "../../components/meetings/MeetingPeek";
import { MeetingsCalendar, isTyping } from "../../components/meetings/MeetingsCalendar";
import { NewMeetingDialog } from "../../components/meetings/NewMeetingDialog";
import { RecentFeed } from "../../components/meetings/RecentFeed";
import { cn } from "../../components/ui/cn";

/**
 * Meetings: Recent, Today and Upcoming along the top, and the calendar or the
 * feed underneath, with a meeting opening in a pane beside whichever it was.
 *
 * The three buttons are the three questions a field rep asks of their diary.
 * **Recent** is what has happened and still needs writing up -- the feed.
 * **Today** is the calendar's day view on today; **Upcoming** is its week (or
 * month, once switched) -- the calendar is one calendar, and the buttons are
 * the two places in it people go most.
 *
 * A meeting opened from any of them keeps the view behind it: the view lives
 * in the query string and the meeting's URL carries it, so closing the pane
 * lands exactly where it was opened -- same week, same scroll, same filter.
 * Both `/meetings` and `/meetings/:id` render this page, so React keeps it
 * mounted while the pane comes and goes.
 *
 * On a desktop the calendar's click is a card first, as in Google Calendar:
 * `MeetingPeek` over the grid, with the pane behind its pencil, envelope and
 * notes. The card is not in the URL -- it is a glance, and Back should not
 * have to step through every meeting somebody glanced at. The feed's click
 * still opens the pane, because a row in the feed is opened to be written up.
 */
export function MeetingsHome({ user }: { user: User }) {
  const { state, set } = useMeetingsView(user.id);
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [slot, setSlot] = useState<{ start: Date; minutes: number } | null>(null);
  const [peek, setPeek] = useState<{ meeting: SalesMeeting; anchor: DOMRect } | null>(null);

  const openId = meetingId && /^\d+$/.test(meetingId) ? Number(meetingId) : null;
  const focus = (location.state as { focus?: PanelFocus } | null)?.focus;

  const users = useQuery({
    queryKey: queryKeys.users,
    queryFn: api.users,
    staleTime: 5 * 60_000,
    enabled: user.role === "admin",
  });
  const whoLabel =
    state.who === "mine"
      ? "My meetings"
      : state.who === "team"
        ? "Team"
        : (users.data?.find((person) => person.id === state.who)?.name ?? "Someone");

  const openMeeting = useCallback(
    (id: number, nextFocus?: PanelFocus) =>
      navigate(`/meetings/${id}${location.search}`, {
        state: nextFocus ? { focus: nextFocus } : null,
        /* Moving from one open meeting to the next replaces rather than
           stacks, so Back closes the pane instead of walking through every
           meeting looked at on the way. */
        replace: openId !== null,
      }),
    [navigate, location.search, openId],
  );

  const close = useCallback(
    () => navigate(`/meetings${location.search}`),
    [navigate, location.search],
  );

  /* Escape closes the pane, as in Gmail -- from the notes too, which save as
     the pane closes -- unless a dialog is open over it, which takes the
     Escape for itself, or the cursor is in a form field such as search. */
  useEffect(() => {
    if (openId === null) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      if (document.querySelector("[role=dialog]")) return;
      const target = event.target as HTMLElement | null;
      if (isTyping(target) && !target?.isContentEditable) return;
      close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openId, close]);

  const onOpen = useCallback(
    (meeting: SalesMeeting, nextFocus?: "notes" | "follow-up") => openMeeting(meeting.id, nextFocus),
    [openMeeting],
  );

  /* The card on a desktop, where there is a grid to keep beside it; the pane
     on anything narrower, where the pane is the whole screen anyway. The
     breakpoint is the pane's own -- below it, the pane is not beside anything. */
  const onCalendarOpen = useCallback(
    (meeting: SalesMeeting, anchor: DOMRect) => {
      if (window.matchMedia("(min-width: 1024px)").matches) setPeek({ meeting, anchor });
      else openMeeting(meeting.id);
    },
    [openMeeting],
  );
  const closePeek = useCallback(() => setPeek(null), []);

  /* A card belongs to the week it was opened on. */
  useEffect(() => setPeek(null), [location.search]);

  /* Today is lit only on today itself: a day view of last Tuesday is the
     calendar, not "today". */
  const tab =
    state.mode === "recent"
      ? "recent"
      : state.mode === "day"
        ? state.date.toDateString() === new Date().toDateString()
          ? "today"
          : null
        : "upcoming";

  return (
    <div className="flex h-full min-h-0">
      <div className="flex min-w-0 flex-1 flex-col">
        {/*
          One line at every width, never a wrapped second row. On a phone the
          three buttons keep their size and the whose-diary picker is what
          gives way -- its value is nearly always "My meetings", so it can lose
          the end of its label before anything is pushed off the screen.

          The picker's text is drawn by a span, not by the select, so it can be
          truncated to fit beside the buttons. The real select is laid over the
          span, invisible but still the thing tapped, so the native picker
          still opens.
        */}
        <div className="flex items-center gap-2 border-b border-line px-2 py-2 sm:px-3">
          <nav className="flex shrink-0 gap-1" aria-label="Meetings">
            {(
              [
                ["recent", "Recent", { view: "recent" }],
                ["today", "Today", { view: "day", date: null }],
                ["upcoming", "Upcoming", { view: "week", date: null }],
              ] as const
            ).map(([key, label, patch]) => (
              <button
                key={key}
                type="button"
                aria-pressed={tab === key}
                onClick={() => set({ ...patch })}
                className={cn(
                  "h-8 rounded-md px-2.5 text-xs transition-colors sm:px-4 sm:text-sm",
                  tab === key
                    ? "bg-brand-600 font-medium text-white"
                    : "border border-line bg-surface text-muted hover:bg-surface-2 hover:text-content",
                )}
              >
                {label}
              </button>
            ))}
          </nav>

          {/* Whose diary. Yours by default; the team's for seeing who is where;
              one named rep's for a manager. */}
          <label className="relative ml-auto flex h-8 min-w-0 items-center gap-1 rounded-md border border-line bg-surface px-2 text-xs text-content focus-within:ring-2 focus-within:ring-brand-500/40 sm:text-sm">
            <span className="truncate">{whoLabel}</span>
            <svg viewBox="0 0 24 24" className="size-3.5 shrink-0 text-faint" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
              <path d="M6 9l6 6 6-6" />
            </svg>
            <select
              value={String(state.who)}
              onChange={(event) => set({ who: event.target.value === "mine" ? null : event.target.value })}
              aria-label="Whose meetings"
              className="absolute inset-0 size-full cursor-pointer appearance-none opacity-0"
            >
              <option value="mine">My meetings</option>
              <option value="team">Team</option>
              {user.role === "admin"
                ? (users.data ?? [])
                    .filter((person) => person.id !== user.id)
                    .map((person) => (
                      <option key={person.id} value={String(person.id)}>
                        {person.name}
                      </option>
                    ))
                : null}
            </select>
          </label>
        </div>

        <div className="min-h-0 flex-1">
          {state.mode === "recent" ? (
            <RecentFeed state={state} set={set} selectedId={openId} onOpen={onOpen} />
          ) : (
            <MeetingsCalendar
              user={user}
              view={state.mode}
              state={state}
              set={set}
              selectedId={peek?.meeting.id ?? openId}
              onOpen={onCalendarOpen}
              onCreate={(start, minutes) => setSlot({ start, minutes })}
            />
          )}
        </div>
      </div>

      {openId !== null ? (
        <MeetingPanel
          key={openId}
          id={openId}
          user={user}
          focus={focus}
          onClose={close}
          onOpenMeeting={(id) => openMeeting(id)}
        />
      ) : null}

      {peek ? (
        <MeetingPeek
          key={peek.meeting.id}
          meeting={peek.meeting}
          anchor={peek.anchor}
          user={user}
          onClose={closePeek}
          onOpen={(nextFocus) => {
            setPeek(null);
            openMeeting(peek.meeting.id, nextFocus);
          }}
        />
      ) : null}

      {slot ? (
        <NewMeetingDialog
          onClose={() => setSlot(null)}
          currentUserId={user.id}
          slot={slot}
        />
      ) : null}
    </div>
  );
}
